import { useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import styled from "@emotion/styled";

import { useLanguageTranslator } from "../../components";
import { ConditionalRenderer } from "../../components";
import { imageCheck, onlyPDF } from "./helpers";
import { AttachmentUploadIcon } from "../../@lib/icons";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function Attachment({
  setAttachmentDetails,
  allowedFormats,
  text,
  variant,
  isDocument = false,
  attachmentObject,
  setAttachmentObject,
}: any) {
  const [message, setMessage] = useState("");

  const { translator } = useLanguageTranslator();

  function handleOnChange(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    const isAllowedFormat = allowedFormats.some((format: string) =>
      file?.type?.toLowerCase()?.includes(format?.toLowerCase())
    );

    if (!isAllowedFormat) {
      setAttachmentDetails(null);
      setAttachmentObject({});
      return setMessage("Please Upload the Correct Format.");
    }

    // for image attachment is below handler
    imageCheck({
      file,
      setMessage,
      setAttachmentDetails,
      setAttachmentObject: setAttachmentObject,
    });

    // for PDF attachment is below handler
    if (allowedFormats?.includes("pdf") && file) {
      onlyPDF({
        file,
        setMessage,
        setAttachmentDetails,
        setAttachmentObject: setAttachmentObject,
      });
      return;
    }
  }

  function handleDrop(event: any) {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (!file) return;

    const isAllowedFormat = allowedFormats.some((format: string) =>
      file?.type?.toLowerCase()?.includes(format?.toLowerCase())
    );

    if (!isAllowedFormat) {
      setAttachmentDetails(null);
      setAttachmentObject({});
      return setMessage("Please Upload the Correct Format.");
    }

    // for document attachment is below handler
    if (isDocument) {
      onlyPDF({ file, setMessage, setAttachmentDetails, setAttachmentObject });
      return;
    }

    // for image attachment is below handler
    imageCheck({ file, setMessage, setAttachmentDetails, setAttachmentObject });

    // for PDF attachment is below handler
    if (allowedFormats?.includes("pdf") && file) {
      onlyPDF({ file, setMessage, setAttachmentDetails, setAttachmentObject });
      return;
    }
  }

  function handleDragOver(event: any) {
    event.preventDefault();
  }

  function handleClose(
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) {
    if (reason === "clickaway") {
      return;
    }

    setMessage("");
  }

  return (
    <>
      <Snackbar
        open={message ? true : false}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={handleClose}
      >
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
      <Button
        component="label"
        role={undefined}
        tabIndex={-1}
        sx={{ width: "100%", padding: "0px" }}
      >
        <VisuallyHiddenInput type="file" onChange={handleOnChange} />
        <Paper
          component={Stack}
          sx={{ py: variant?.py || 6, mb: 2, width: "100%" }}
          alignItems="center"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          variant={variant?.variant || ""}
        >
          <Avatar sx={{ mb: 2 }}>
            <AttachmentUploadIcon />
          </Avatar>
          <ConditionalRenderer condition={!attachmentObject?.name}>
            <Typography mb={0.5}>
              {translator("Click to upload or drag and drop")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {text}
            </Typography>
          </ConditionalRenderer>
          <ConditionalRenderer condition={attachmentObject?.name}>
            <Typography mb={0.5}>
              {attachmentObject?.name?.slice(0, 50)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {text}
            </Typography>
          </ConditionalRenderer>
        </Paper>
      </Button>
    </>
  );
}

export { Attachment };
